import { useCurrentStep } from "../../../hooks/track/useCurrentStep";
import { useClientTranslations } from "../../../lib/i18n/client";
import { getCurrentStepAsset } from "../../../lib/track/currentStepAssets";
import type { JourneyStep } from "../../../types/journey";
import styles from "./CurrentStepCard.module.css";

/**
 * CurrentStepCard (CSC-01, CSC-03).
 *
 * Renders the patient's current cycle step (thumbnail + title + subtext).
 *
 * M011/S04 T04: when an in-progress JourneyStep is passed in, source the
 * title/subtext from that step (single source-of-truth with Get Ready and
 * Journey). The legacy /api/current-step hook is only consulted as a fallback
 * for the first paint before useJourneyProgress resolves. Title/subtext are
 * looked up via i18n keys `currentStep.{id}.title` / `currentStep.{id}.subtext`
 * (matching the legacy key shape) with a fallback to the step's English copy.
 *
 * Failure model is non-blocking: a /api/current-step failure does NOT trip
 * the page-level error state (TRACK-03 only triggers on the events fetch).
 * On `isLoading` the card renders a skeleton block; on `isError` it renders
 * nothing (silent fallback).
 */
interface CurrentStepCardProps {
	journeyStep?: JourneyStep | null;
}

export function CurrentStepCard({ journeyStep }: CurrentStepCardProps = {}) {
	const { t } = useClientTranslations("track");
	const { data, isLoading, isError } = useCurrentStep();

	if (journeyStep) {
		const i18nTitleKey = `currentStep.${journeyStep.id}.title`;
		const i18nSubtextKey = `currentStep.${journeyStep.id}.subtext`;
		const titleResolved = t(i18nTitleKey, { defaultValue: journeyStep.title });
		const subtextResolved = t(i18nSubtextKey, {
			defaultValue: journeyStep.description,
		});
		const thumbnailUrl =
			getCurrentStepAsset(journeyStep.id) ??
			data?.thumbnailUrl ??
			"/images/track-thumbnail.png";

		return (
			<article className={styles.card}>
				<img
					src={thumbnailUrl}
					alt=""
					className={styles.thumbnail}
					loading="lazy"
				/>
				<div className={styles.body}>
					<h3 className={styles.title}>{titleResolved}</h3>
					<p className={styles.subtext}>{subtextResolved}</p>
				</div>
			</article>
		);
	}

	if (isLoading) {
		return <div className={styles.skeleton} aria-busy="true" />;
	}
	if (isError || !data) {
		return null;
	}

	const i18nTitleKey = `currentStep.${data.id}.title`;
	const i18nSubtextKey = `currentStep.${data.id}.subtext`;
	const titleResolved = t(i18nTitleKey, { defaultValue: data.title });
	const subtextResolved = t(i18nSubtextKey, { defaultValue: data.subtext });

	return (
		<article className={styles.card}>
			<img
				src={data.thumbnailUrl}
				alt=""
				className={styles.thumbnail}
				loading="lazy"
			/>
			<div className={styles.body}>
				<h3 className={styles.title}>{titleResolved}</h3>
				<p className={styles.subtext}>{subtextResolved}</p>
			</div>
		</article>
	);
}
